import React, { useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { Button } from '@components/main-components/button/Button';
import { useMediaQuery } from '@context/mediaQuery';
import { ParamsStore } from '@context/params';
import type { TierNft } from '@graphql/reactQuery';
import { scrollToTop } from '@utils/helpers';
import { TAB_CHOICES } from '../Activity/store';
import {
  DEFAULT_MOBILE_EMPTY_SEAT_COUNT_V2,
  DEFAULT_TABLET_EMPTY_SEAT_COUNT_V2,
} from '../Audience/shared/constants';
import { AudienceSeatNew, EmptySeat } from './Seat';
import type { AudienceSortType } from './constants';
import { useAudienceV2 } from './useAudience';

export function Audience({
  releaseId,
  tier,
  sort,
  paginate,
}: {
  releaseId: string;
  tier: TierNft;
  sort: AudienceSortType;
  paginate: boolean;
}) {
  const { tablet } = useMediaQuery();

  const [bottomRef, isAtBottom] = useInView({
    threshold: 0.1,
  });

  const { collectors, initialCollectors, hasNextPage, loading, fetchMore } = useAudienceV2({
    releaseId,
    tier,
    sort,
  });

  useEffect(() => {
    if (!loading && paginate && isAtBottom && hasNextPage) {
      fetchMore();
    }
  }, [isAtBottom, loading, fetchMore, paginate, hasNextPage]);

  const collectorProfiles = useMemo(() => {
    if (!collectors.length) return;

    if (paginate) {
      return collectors.map((collector, i) => (
        <AudienceSeatNew
          seatFrag={collector}
          index={i}
          key={`audience-seat-${collector.id}`}
          tier={tier}
          sort={sort}
        />
      ));
    }

    return initialCollectors.map((collector, i) => (
      <AudienceSeatNew
        seatFrag={collector}
        index={i}
        key={`audience-seat-${collector.id}`}
        tier={tier}
        sort={sort}
      />
    ));
  }, [collectors, paginate, initialCollectors, tier, sort]);

  // TODO: Add proper virtualization to audience https://linear.app/sound/issue/ENG-8305/investigate-virtualization-in-audience-section
  return (
    <>
      <div className="grid w-full grid-cols-[repeat(18,1fr)] gap-2 overflow-visible md:gap-3">
        {!collectors.length ? (
          Array.from({
            length: tablet
              ? DEFAULT_TABLET_EMPTY_SEAT_COUNT_V2
              : DEFAULT_MOBILE_EMPTY_SEAT_COUNT_V2,
          }).map((_, i) => (
            <EmptySeat index={i} key={`empty-seat-${i}`} tier={tier} loading={loading} />
          ))
        ) : (
          <>
            {collectorProfiles}
            <div ref={bottomRef} />
          </>
        )}
      </div>

      {hasNextPage && !paginate && (
        <div className="mt-3 flex w-full items-center justify-center">
          <Button
            label="See All"
            variant="tertiary"
            size="S"
            onClick={() => {
              scrollToTop();
              ParamsStore.song_tab = TAB_CHOICES.AUDIENCE;
            }}
          />
        </div>
      )}
    </>
  );
}
